import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Validators } from '@angular/forms';
import {
  ADVANCED_SETTINGS_EXPANSION_PANEL_CONFIG,
  ASSET_EXPANSION_PANEL_CONFIG,
  BASIC_SETTINGS_EXPANSION_PANEL_CONFIG,
  CUSTOM_EVENTS_EXPANSION_PANEL_CONFIG,
  inheritConfigsMapping,
  SPEED_SIGN_NUMBER_AUDIO_ALERTS,
  nestedObjectInConfigsKeyMappings,
  nestedobjToShowKeyList,
  PANIC_ACTION_MAPPINGS,
  PANIC_BUTTON_ACTIONS,
  VIOLATION_EVENT_KEYS_FOR_DEFAULT_MAPPING,
  UNIT_CONVERSION_REQUIRED_KEYS,
} from '@app-asset-config/common/asset-configuration.constants';
import { API } from '@app-core/constants/api.constants';
import { DefaultConfigurationBody, KeyValuePair } from '@app-core/models/core.model';
import { AccessService } from '@app-core/services/access/access.service';
import { HttpCacheService } from '@app-core/services/cache/cache.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  AssetConfigurationParams,
  FleetConfigurationParams,
  GetAssetsParams,
  GetAudioAlertListParams,
  GetAudioAlertListResponse,
} from '../common/asset-configuration.model';
import { configEvents } from '@app-asset-config/models/tagging.model';
import { DataService } from '@app-core/services/data/data.service';

@Injectable({
  providedIn: 'root',
})
export class AssetConfigurationService {
  public isLoadingConfig = new BehaviorSubject<boolean>(false);
  public isUpdatingConfig = new BehaviorSubject<boolean>(false);
  public isCustomUpdatingConfig = new BehaviorSubject<boolean>(false);
  public currentDutyTypeConfig = new BehaviorSubject({});
  public currentDutyType = new BehaviorSubject<string>('heavy');
  public currentCustomDutyType = new BehaviorSubject<string>('heavy');
  public isBasicConfigDirty = new BehaviorSubject<boolean>(false);
  public isAdvancedConfigDirty = new BehaviorSubject<boolean>(false);
  public currentAssetConfig = new BehaviorSubject({});
  public isAssetConfigDirty = new BehaviorSubject<boolean>(false);
  public currentDutyTypeConfigCustomEvents = new BehaviorSubject({});
  public isCustomConfigDirty = new BehaviorSubject<boolean>(false);
  private customSettingExpansionPanelConfigCache: Map<any, any> = new Map<any, any>();
  public resetDirtyBaseline$ = new Subject<void>();

  constructor(
    private _http: HttpClient,
    private fb: FormBuilder,
    private accessService: AccessService,
    private cacheService: HttpCacheService,
    public dataService: DataService
  ) {}

  public flattenObject(obj: KeyValuePair) {
    const flattendObj = {};
    Object.entries(obj || {}).forEach(([key, value]) => {
      if (!Array.isArray(value) && typeof value === 'object' && value !== null) {
        Object.assign(flattendObj, this.flattenObject(value as any));
      } else {
        flattendObj[key] = value;
      }
    });
    return flattendObj;
  }

  private inheritSpeedingConfigs(config: KeyValuePair): KeyValuePair {
    return {
      postEventVideoDurationMaxSpeed: config.postEventVideoDurationSpeeding,
      eventVideoQualityMaxSpeed: config.eventVideoQualitySpeeding,
      eventVideoDriverCameraQualityMaxSpeed: config.eventVideoDriverCameraQualitySpeeding,
      eventVideoResolutionMaxSpeed: config.eventVideoResolutionSpeeding,
      eventVideoDriverCameraResolutionMaxSpeed: config.eventVideoDriverCameraResolutionSpeeding,
      maxSpeedEventVideoType: config.speedingEventVideoType,
      eventMediaTypeMaxSpeed: config.eventMediaTypeSpeeding,
      maxSpeedEDVREnabled: config.speedingEDVREnabled,
    };
  }

  private inheritDistractedDrivingConfigs(config: KeyValuePair): KeyValuePair {
    return {
      // cellphone
      preEventVideoDurationCellphoneDistraction: config.preEventVideoDurationDistractedDriving,
      postEventVideoDurationCellphoneDistraction: config.postEventVideoDurationDistractedDriving,
      eventVideoQualityCellphoneDistraction: config.eventVideoQualityDistractedDriving,
      eventVideoDriverCameraQualityCellphoneDistraction: config.eventVideoDriverCameraQualityDistractedDriving,
      eventVideoResolutionCellphoneDistraction: config.eventVideoResolutionDistractedDriving,
      eventVideoDriverCameraResolutionCellphoneDistraction: config.eventVideoDriverCameraResolutionDistractedDriving,
      cellphoneDistractionEventVideoType: config.distractedDrivingEventVideoType,
      cellphoneDistractionEDVREnabled: config.distractedDrivingEDVREnabled,
      captureMediaOnEventCellphoneDistraction: config.captureMediaOnEventDistractedDriving,

      // lizard-eye
      preEventVideoDurationLizardEyeDistraction: config.preEventVideoDurationDistractedDriving,
      postEventVideoDurationLizardEyeDistraction: config.postEventVideoDurationDistractedDriving,
      eventVideoQualityLizardEyeDistraction: config.eventVideoQualityDistractedDriving,
      eventVideoDriverCameraQualityLizardEyeDistraction: config.eventVideoDriverCameraQualityDistractedDriving,
      eventVideoResolutionLizardEyeDistraction: config.eventVideoResolutionDistractedDriving,
      eventVideoDriverCameraResolutionLizardEyeDistraction: config.eventVideoDriverCameraResolutionDistractedDriving,
      lizardEyeDistractionEventVideoType: config.distractedDrivingEventVideoType,
      lizardEyeDistractionEDVREnabled: config.distractedDrivingEDVREnabled,
      captureMediaOnEventLizardEyeDistraction: config.captureMediaOnEventDistractedDriving,

      // smoking
      preEventVideoDurationSmokingDistraction: config.preEventVideoDurationDistractedDriving,
      postEventVideoDurationSmokingDistraction: config.postEventVideoDurationDistractedDriving,
      eventVideoQualitySmokingDistraction: config.eventVideoQualityDistractedDriving,
      eventVideoDriverCameraQualitySmokingDistraction: config.eventVideoDriverCameraQualityDistractedDriving,
      eventVideoResolutionSmokingDistraction: config.eventVideoResolutionDistractedDriving,
      eventVideoDriverCameraResolutionSmokingDistraction: config.eventVideoDriverCameraResolutionDistractedDriving,
      smokingDistractionEventVideoType: config.distractedDrivingEventVideoType,
      smokingDistractionEDVREnabled: config.distractedDrivingEDVREnabled,
      captureMediaOnEventSmokingDistraction: config.captureMediaOnEventDistractedDriving,

      // drinking
      preEventVideoDurationDrinkingDistraction: config.preEventVideoDurationDistractedDriving,
      postEventVideoDurationDrinkingDistraction: config.postEventVideoDurationDistractedDriving,
      eventVideoQualityDrinkingDistraction: config.eventVideoQualityDistractedDriving,
      eventVideoDriverCameraQualityDrinkingDistraction: config.eventVideoDriverCameraQualityDistractedDriving,
      eventVideoResolutionDrinkingDistraction: config.eventVideoResolutionDistractedDriving,
      eventVideoDriverCameraResolutionDrinkingDistraction: config.eventVideoDriverCameraResolutionDistractedDriving,
      drinkingDistractionEventVideoType: config.distractedDrivingEventVideoType,
      drinkingDistractionEDVREnabled: config.distractedDrivingEDVREnabled,
      captureMediaOnEventDrinkingDistraction: config.captureMediaOnEventDistractedDriving,

      // texting
      preEventVideoDurationTextingDistraction: config.preEventVideoDurationDistractedDriving,
      postEventVideoDurationTextingDistraction: config.postEventVideoDurationDistractedDriving,
      eventVideoQualityTextingDistraction: config.eventVideoQualityDistractedDriving,
      eventVideoDriverCameraQualityTextingDistraction: config.eventVideoDriverCameraQualityDistractedDriving,
      eventVideoResolutionTextingDistraction: config.eventVideoResolutionDistractedDriving,
      eventVideoDriverCameraResolutionTextingDistraction: config.eventVideoDriverCameraResolutionDistractedDriving,
      textingDistractionEventVideoType: config.distractedDrivingEventVideoType,
      textingDistractionEDVREnabled: config.distractedDrivingEDVREnabled,
      captureMediaOnEventTextingDistraction: config.captureMediaOnEventDistractedDriving,
    };
  }

  private inheritDrowsyDriverConfigs(config: KeyValuePair): KeyValuePair {
    return {
      preEventVideoDurationDriverFatigue: config.preEventVideoDurationDrowsyDriving,
      postEventVideoDurationDriverFatigue: config.postEventVideoDurationDrowsyDriving,
      eventVideoQualityDriverFatigue: config.eventVideoQualityDrowsyDriving,
      eventVideoDriverCameraQualityDriverFatigue: config.eventVideoDriverCameraQualityDrowsyDriving,
      eventVideoResolutionDriverFatigue: config.eventVideoResolutionDrowsyDriving,
      eventVideoDriverCameraResolutionDriverFatigue: config.eventVideoDriverCameraResolutionDrowsyDriving,
      driverFatigueEventVideoType: config.drowsyDrivingEventVideoType,
      driverFatigueCoolingOffDurationInSeconds: config.drowsinessCoolingOffDurationInSeconds,
      maximumDriverFatigueEventsPerTrip: config.maximumDrowsinessEventsPerTrip,
      driverFatigueEDVREnabled: config.drowsyDrivingEDVREnabled,
      captureMediaOnEventDriverFatigue: config.captureMediaOnEventDrowsyDriving,
    };
  }

  private transformToNestedObjects(parent: any, keyMappings: { [nestedObjectKey: string]: { [key: string]: string } }) {
    for (const nestedObjectKey in keyMappings) {
      const nestedObject: any = {};
      const keyMapping = keyMappings[nestedObjectKey];

      for (const key in keyMapping) {
        if (parent.hasOwnProperty(key)) {
          const newKey = keyMapping[key];
          nestedObject[newKey] = parent[key];
          parent[nestedObjectKey] = nestedObject;
          delete parent[key];
        }
      }
    }

    return parent;
  }

  public getChangedConfigs(originalConfig: KeyValuePair = {}, newConfig: KeyValuePair = {}, isCustomConfig?: boolean): KeyValuePair {
    this.transformToNestedObjects(newConfig, nestedObjectInConfigsKeyMappings);

    // 🔌 Build GPIO config list for payload
    const gpioConfigListFromTrigger = this.buildGpioConfigListForPayload(newConfig);
    const hadGpioBefore = Array.isArray(originalConfig.gpioConfigList) && originalConfig.gpioConfigList.length > 0;
    const disabledNow = newConfig.gpIoTriggerEnabled === false;

    if (gpioConfigListFromTrigger) {
      // ✅ Always send the built list, regardless of enabled true/false
      newConfig.gpioConfigList = gpioConfigListFromTrigger;
    } else if (disabledNow && hadGpioBefore) {
      // ✅ Toggle OFF, no new values in form – reuse previous config,
      // but mark enabled: false
      newConfig.gpioConfigList = originalConfig.gpioConfigList.map((g: any) => ({
        ...g,
        enabled: false,
      }));
    } else {
      // No change to GPIO → don't send gpioConfigList
      delete newConfig.gpioConfigList;
    }

    // ❗ All the "flat" GPIO keys that we do NOT want to send separately
    const GPIO_FLAT_KEYS = new Set<string>([
      'gpIoTriggerEnabled',
      'gpIoSource',
      'maxSpeedThreshold',
      'minSpeedThreshold',
      'dmsEventsForGpioTrigger',
      'gpioPinDurationInSeconds',
      'deviceOutPinNumber',
      'gpioDeviceOutPinNumber',
      'gpIoDeviceOutPinNumber',
    ]);

    let changedConfigs = Object.entries(newConfig)
      .filter(([key, value]) => {
        // ⛔ Skip individual flat GPIO keys entirely — backend should only see gpioConfigList
        if (GPIO_FLAT_KEYS.has(key)) {
          return false;
        }

        // ✅ GPIO: diff only on gpioConfigList when present
        if (key === 'gpioConfigList') {
          // ✅ FIXED: No normalization during comparison - compare as-is
          const orig = originalConfig[key] || [];
          const curr = (value as any[]) || [];
          return JSON.stringify(orig) !== JSON.stringify(curr);
        }

        // Ignore any future nested gpioTrigger, if added
        if (key === 'gpioTrigger') {
          return false;
        }

        if (key === 'audioAlertsEnabled') {
          if (Array.isArray(originalConfig[key]) && Array.isArray(value) && originalConfig[key].length !== value.length) {
            return true;
          }
        }

        if (Array.isArray(originalConfig[key]) && Array.isArray(value) && originalConfig[key].length !== value.length) {
          return true;
        } else if (typeof originalConfig[key] !== 'object' && typeof value !== 'object' && originalConfig[key] !== value) {
          return true;
        } else if (typeof value === 'object' && nestedobjToShowKeyList.includes(key)) {
          let nestedObjInOriginalConfig: KeyValuePair = [];
          let nestedObjInNewConfig: KeyValuePair = [];
          nestedObjInNewConfig.push({ [key]: value });
          nestedObjInOriginalConfig.push({ [key]: originalConfig[key] });
          return this.areNestedObjectsDifferent(nestedObjInNewConfig, nestedObjInOriginalConfig);
        } else if (VIOLATION_EVENT_KEYS_FOR_DEFAULT_MAPPING.includes(key) && value === null && originalConfig[key] !== value) {
          return true;
        } else {
          return false;
        }
      })
      .map(([key, value]) => {
        let updatedValue: any;

        // ✅ GPIO: pass through list exactly
        if (key === 'gpioConfigList') {
          updatedValue = value;
          return { [key]: updatedValue };
        }

        // ... rest of the method remains the same
        if (key === 'ignoreSchoolZoneSpeedingViolations') {
          updatedValue = !value;
        } else if (key === 'audioAlertsEnabled') {
          updatedValue = [...(value as Array<string>), ...SPEED_SIGN_NUMBER_AUDIO_ALERTS];
        } else if (key === 'captureMediaOnEventMaxSpeed' && !!value) {
          return {
            [key]: newConfig.captureMediaOnEventSpeeding,
            ...(newConfig.captureMediaOnEventSpeeding ? this.inheritSpeedingConfigs(newConfig) : {}),
          };
        } else if (key === 'postedAndMaxSpeedEnabled' && value === false) {
          return {
            speedingEnabled: value,
            maxSpeedEnabled: value,
            postedAndMaxSpeedEnabled: undefined,
            ...(newConfig.speedingEnabled ? this.inheritSpeedingConfigs(newConfig) : {}),
          };
        } else if (key === 'distractedAndSubEventsEnabled' && value === false) {
          return {
            distractedDrivingEnabled: value,
            enableCellphoneDistraction: value,
            distractedAndSubEventsEnabled: undefined,
            ...(newConfig.distractedDrivingEnabled ? this.inheritDistractedDrivingConfigs(newConfig) : {}),
          };
        } else if (key === 'drowsyAndSubEventsEnabled' && value === false) {
          return {
            drowsyDrivingEnabled: value,
            drowsyAndSubEventsEnabled: undefined,
            ...(newConfig.drowsyDrivingEnabled ? this.inheritDrowsyDriverConfigs(newConfig) : {}),
          };
        } else if (key === 'speedingDurationEnabled' && !value) {
          return {
            speedingDurationEnabled: value,
            speedingDuration: undefined,
          };
        } else if (
          Object.keys(inheritConfigsMapping).includes(key) &&
          (!inheritConfigsMapping[key].condition || inheritConfigsMapping[key].condition(value))
        ) {
          return {
            [key]: value,
            [inheritConfigsMapping[key].key]: value,
            ...(Object(inheritConfigsMapping[key]).hasOwnProperty('key2') ? { [inheritConfigsMapping[key].key2]: value } : {}),
            ...(Object(inheritConfigsMapping[key]).hasOwnProperty('key3') ? { [inheritConfigsMapping[key].key3]: value } : {}),
            ...(Object(inheritConfigsMapping[key]).hasOwnProperty('key4') ? { [inheritConfigsMapping[key].key4]: value } : {}),
            ...(Object(inheritConfigsMapping[key]).hasOwnProperty('key5') ? { [inheritConfigsMapping[key].key5]: value } : {}),
          };
        } else if (PANIC_BUTTON_ACTIONS.includes(key)) {
          updatedValue = PANIC_ACTION_MAPPINGS[value];
        } else if (key === 'potentialCrashSpeedThreshold') {
          updatedValue = !value ? -10 : value;
        } else if (key === 'potentialCrashThreshold') {
          updatedValue = value * 9.80665;
        } else {
          updatedValue = value;
        }

        if (isCustomConfig) {
          return { [key]: updatedValue };
        }

        return {
          [key]: updatedValue,
          postedAndMaxSpeedEnabled: undefined,
          distractedAndSubEventsEnabled: undefined,
          drowsyAndSubEventsEnabled: undefined,
        };
      })
      .reduce((a, b) => ({ ...a, ...b }), {});

    return changedConfigs;
  }

  private areNestedObjectsDifferent(obj1: KeyValuePair, obj2: KeyValuePair): boolean {
    if (obj1.length !== obj2.length) {
      return true;
    }
    for (let i = 0; i < obj1.length; i++) {
      const value1 = obj1[i][Object.keys(obj1[i])[0]];
      const value2 = obj2[i][Object.keys(obj2[i])[0]];

      if (!this.areValuesEqual(value1, value2)) {
        return true;
      }
    }

    return false;
  }

  private areValuesEqual(value1: any, value2: any): boolean {
    if (typeof value1 === 'object' && typeof value2 === 'object') {
      return JSON.stringify(value1) === JSON.stringify(value2);
    }
    return value1 === value2;
  }

  public get basicSettingsExpansionPanelConfig(): any {
    const { customerName = '' } = this.accessService.getLoginInfo() || {};
    return BASIC_SETTINGS_EXPANSION_PANEL_CONFIG(customerName);
  }

  public get advancedSettingsExpansionPanelConfig(): any {
    return ADVANCED_SETTINGS_EXPANSION_PANEL_CONFIG;
  }

  private cleanFloat(num: Number) {
    // JS and other languages have a problem with floating point precision
    // this is a workaround to fix it
    // https://stackoverflow.com/questions/588004/is-floating-point-math-broken

    return parseFloat(Number(num).toPrecision(12));
  }

  public convertSpecialValue(key, value) {
    if (key === 'ignoreSchoolZoneSpeedingViolations') {
      return !value;
    } else if (key === 'audioAlertsEnabled' && Array.isArray(value)) {
      return [...((value || []) as Array<string>).filter((v) => !SPEED_SIGN_NUMBER_AUDIO_ALERTS.includes(v))];
    } else if (key === 'potentialCrashSpeedThreshold') {
      return Math.max(0, value);
    } else if (key === 'potentialCrashThreshold') {
      return this.cleanFloat(value / 9.80665);
    } else if (PANIC_BUTTON_ACTIONS.includes(key)) {
      return this.mapPanicButtonAction(value);
    }

    return value;
  }

  public formatConfig(dutyTypeConfig = {}, originalConfig = {}) {
    const formattedConfig = Object.entries(dutyTypeConfig)
      .map(([key, value]: [string, any]) => {
        return {
          [key]: this.convertSpecialValue(key, value),
        };
      })
      .reduce((a, b) => ({ ...a, ...b }), {});
    const updatedNestedObjects = this.getUpdatedNestedObjects(dutyTypeConfig);
    const {
      speedingEnabled = false,
      maxSpeedEnabled = false,
      distractedDrivingEnabled = false,
      enableCellphoneDistraction = false,
      drowsyDrivingEnabled = false,
      enableDriverFatigueDetection = false,
    } = { ...(originalConfig || {}), ...(formattedConfig || {}) };

    const postedAndMaxSpeedEnabled = speedingEnabled || maxSpeedEnabled;
    const distractedAndSubEventsEnabled = distractedDrivingEnabled || enableCellphoneDistraction;
    const drowsyAndSubEventsEnabled = drowsyDrivingEnabled || enableDriverFatigueDetection;

    return {
      ...formattedConfig,
      ...updatedNestedObjects,
      postedAndMaxSpeedEnabled,
      distractedAndSubEventsEnabled,
      drowsyAndSubEventsEnabled,
    };
  }

  private mapPanicButtonAction(action) {
    let mappedValue;
    if (Array.isArray(action)) {
      mappedValue = Object.entries(PANIC_ACTION_MAPPINGS).find(([_, value]) => this.areArraysSame(action, value));
    }

    return mappedValue?.[0] || 'eventCapture';
  }

  private areArraysSame(arr1, arr2) {
    if (arr1.length !== arr2.length) {
      return false;
    }

    return arr1.every((key) => arr2.includes(key));
  }

  public getUpdatedNestedObjects(dutyTypeConfig: KeyValuePair): KeyValuePair {
    let updatedNestedObjects: KeyValuePair = {};

    nestedobjToShowKeyList.forEach((key) => {
      const value = dutyTypeConfig[key];
      if (typeof value === 'object') {
        const mappings = nestedObjectInConfigsKeyMappings[key];

        if (mappings) {
          Object.entries(mappings).forEach(([newKey, oldKey]) => {
            updatedNestedObjects[newKey] = value[oldKey as keyof typeof value];
          });
        }
      }
    });

    return updatedNestedObjects;
  }

  public get expansionPanelConfig(): any {
    const { customerName = '' } = this.accessService.getLoginInfo() || {};
    return BASIC_SETTINGS_EXPANSION_PANEL_CONFIG(customerName);
  }

  private setParams(params) {
    return new HttpParams({
      fromObject: params,
    });
  }

  private getData(url: string, params: any, options: any = {}) {
    const op = {
      params: this.setParams(params),
      ...options,
    };
    return this._http.get(url, op);
  }

  /**
   * Patch data
   * @description Make Http request with PATCH method
   * @param url API endpoint
   * @param body Request body
   * @param params Other parameters
   */
  private patchData(url: string, body: any, params?: any) {
    const options = {
      params: this.setParams(params),
    };
    return this._http.patch(url, body, options);
  }

  public getAssets(param: GetAssetsParams): Observable<any> {
    return this.getData(API.GET_FLEET_ASSETS, param);
  }

  /**
   * Patch asset info
   * @description Service to add new asset, edit existing asset or to update the status of the exsting asset
   * @param body Request body (Asset to be updated or added)
   * @param param Query params
   */
  public patchAsset(body: any, params?: FleetConfigurationParams): Observable<any> {
    return this.patchData(API.PATCH_ASSET, body, params).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(API.GET_FLEET_ASSETS);
      })
    );
  }

  public getFleetConfiguration(params?: FleetConfigurationParams): Observable<any> {
    return this.getData(API.GET_FLEET_CONFIGURATION, params).pipe(
      map((res) => {
        const { lastUpdated = '', ...configuration } = (res || {}) as any;

        ['light', 'medium', 'heavy'].forEach((key) => {
          if (configuration[key]) {
            // 🔹 Map gpioConfigList → nested gpioTrigger for the form
            configuration[key] = this.mapGpioConfigListToFlat(configuration[key]);

            const {
              enabled = false,
              durationInMinutes = 10,
              maxDailyDurationInMinutes = 60,
              disableLiveTelematics = false,
              disableDriverCamera = false,
              disableExternalCameras = false,
            } = configuration[key].surveillanceTripConfig || {};

            const { enabled: privacyModeEnabled = false, timeLimitInHours = 1 } = configuration[key]?.privacyModeConfiguration || {};

            configuration[key] = {
              ...configuration[key],
              privacyModeConfiguration: {
                enabled: configuration[key]?.privacyModeConfiguration?.enabled || false,
                timeLimitInHours: configuration[key]?.privacyModeConfiguration?.timeLimitInHours || 1,
              },
              surveillanceModeTripEnabled: enabled,
              surveillanceModeTripDurationInMinutes: durationInMinutes,
              surveillanceModeTripMaxDailyDurationInMinutes: maxDailyDurationInMinutes,
              surveillanceModeTripDisableLiveTelematics: disableLiveTelematics,
              surveillanceModeTripDisableDriverCamera: disableDriverCamera,
              surveillanceModeTripDisableExternalCameras: disableExternalCameras,
              privacyModeConfigurationEnabled: privacyModeEnabled,
              privacyModeConfigurationTimeLimitInHours: timeLimitInHours,
            };
          }
        });

        const formattedConfig = Object.entries(configuration)
          .map(([dutyType, dutyTypeConfig]) => {
            return { [dutyType]: this.formatConfig(dutyTypeConfig) };
          })
          .reduce((a, b) => ({ ...a, ...b }), {});

        return {
          ...res,
          configuration: formattedConfig,
        };
      })
    );
  }

  public updateFleetConfiguration(body: any): Observable<any> {
    const url = API.UPDATE_FLEET_CONFIGURATION;
    return this.patchData(url, body).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(API.GET_FLEET_CONFIGURATION);
      })
    );
  }

  public updateDefaultConfiguration(body: DefaultConfigurationBody) {
    const url = API.UPDATE_DEFAULT_CONFIGURATION;
    return this.patchData(url, body);
  }

  private getFormControl(options: any, value: string | number): FormControl {
    const { required = true, min, max } = options || { min: undefined, max: undefined };
    const validators = [];
    if (required) {
      validators.push(Validators.required);
    }
    if (min !== undefined) {
      validators.push(Validators.min(min));
    }
    if (max !== undefined) {
      validators.push(Validators.max(max));
    }
    return this.fb.control(value, validators);
  }

  private getFormArrayControl(options = {} as any, value = []) {
    const { childFormControlClass = 'FormControl', childFormControlConfig = [] } = options;
    const childControlsConfig = value.map((v) => {
      if (childFormControlClass === 'FormGroup') {
        const formGroupConfig = childFormControlConfig
          .map((childConfig) => ({ [childConfig.key]: this.getFormControl(childConfig, v[childConfig.key]) }))
          .reduce((a, b) => ({ ...a, ...b }));
        return this.fb.group(formGroupConfig);
      }
      return this.getFormControl(childFormControlConfig[0], v[childFormControlConfig[0].key]);
    });
    const formArrayControl = this.fb.array(childControlsConfig);
    return formArrayControl;
  }

  private getFormGroupConfig(controls: any[] = [], internalPanel: any = {}, rootConfig: any = {}, panelKey?: string) {
    // 👇 Pick the panel-specific slice if it exists (possibleCollision, device, surveillance, gpioTrigger, etc.)
    const panelConfig =
      panelKey && rootConfig && typeof (rootConfig as any)[panelKey] === 'object' && (rootConfig as any)[panelKey] !== null
        ? (rootConfig as any)[panelKey]
        : rootConfig;

    const formControls = controls.reduce((acc, b: any) => {
      const { formControlClass = 'FormControl' } = b || {};
      const valueSource = panelConfig || {};

      if (formControlClass === 'FormArray') {
        return {
          ...acc,
          [b.key]: this.getFormArrayControl(b, valueSource[b.key]),
        };
      }

      return {
        ...acc,
        [b.key]: this.getFormControl(b, valueSource[b.key]),
      };
    }, {});

    return {
      ...formControls,
      ...(Object.keys(internalPanel).length > 0 ? { internalPanel: this.toFormGroup(internalPanel, rootConfig) } : {}),
    };
  }

  public toFormGroup(expansionPanelConfig: any = {}, config: any = {}): FormGroup {
    const subGroups = Object.entries(expansionPanelConfig).reduce((a, [key, value]) => {
      const { description = [], content = [], internalPanel = {}, additionalContent: { items = [] } = {} } = value as any;

      const controls = description.concat(content, items);
      const formGroupConfig = this.getFormGroupConfig(controls, internalPanel, config, key);

      return {
        ...a,
        [key]: this.fb.group(formGroupConfig),
      };
    }, {});
    return this.fb.group(subGroups);
  }

  public getAssetConfiguration(params?: AssetConfigurationParams): Observable<any> {
    return this.getData(API.GET_ASSET_CONFIGURATION, params).pipe(
      map((res) => {
        const { configuration } = (res || {}) as any;
        return {
          ...res,
          configuration,
        };
      })
    );
  }
  public getAudioAlert(params?: GetAudioAlertListParams): Observable<GetAudioAlertListResponse> {
    return this.getData(API.GET_AUDIO_ALERT, params).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  public get assetExpansionPanelConfig(): any {
    return ASSET_EXPANSION_PANEL_CONFIG;
  }

  public updateAssetConfiguration(body: any, params?: AssetConfigurationParams): Observable<any> {
    const url = API.UPDATE_ASSET_CONFIGURATION;
    return this.patchData(url, body, params).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(API.GET_ASSET_CONFIGURATION);
      })
    );
  }

  public updateDriverPermissions(params: any, body: any): Observable<any> {
    const httpOptions = {
      params: this.setParams(params),
    };
    return this._http.patch(API.UPDATE_DRIVER_PERMISSIONS, body, httpOptions).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(API.GET_DRIVER_PERMISSIONS);
      })
    );
  }

  public getDriverPermissions(params: any): Observable<any> {
    return this.getData(API.GET_DRIVER_PERMISSIONS, params);
  }

  public get customSettingExpansionPanelConfig(): any {
    if (this.customSettingExpansionPanelConfigCache.has(this.currentDutyTypeConfigCustomEvents.value)) {
      return this.customSettingExpansionPanelConfigCache.get(this.currentDutyTypeConfigCustomEvents.value);
    }

    const expandedConfig = CUSTOM_EVENTS_EXPANSION_PANEL_CONFIG(this.currentDutyTypeConfigCustomEvents.value);
    this.customSettingExpansionPanelConfigCache.set(this.currentDutyTypeConfigCustomEvents.value, expandedConfig);
    return expandedConfig;
  }

  public getFleetCustomEvents(): Observable<any> {
    return this._http.get(API.GET_FLEET_CUSTOM_EVENT_CONFIGURATION);
  }

  public getAllEvents(): Observable<any> {
    return this._http.get(API.GET_FLEET_EVENTS_CUSTOM_EVENTS);
  }

  public updateCustomEventsConfiguration(body: any): Observable<any> {
    const url = API.UPDATE_CUSTOM_EVENT_CONFIGURATION;
    return this.patchData(url, body);
  }

  public processSelectedEvents(configArray: configEvents[]): string[] {
    const selectedEvents: string[] = [];
    configArray.forEach((event: configEvents) => {
      if (event.checked) {
        if (event.key === '0') {
          selectedEvents.push('panic-button');
        } else {
          selectedEvents.push(event.key);
        }
        if (event.subEvents?.length > 0) {
          selectedEvents.push(...event.subEvents);
        }
      }
    });
    return selectedEvents;
  }

  /**
   * Recursively maps all matching keys in a deeply nested object.
   *
   * @param data - The object to be modified.
   * @param keys - An array of keys to toggle.
   * @returns A new object with updated values.
   */
  public mapToDefault(data: any, keys: string[], ignoreZero?: boolean): any {
    // function to perform deep key traversal and value mapping
    const mapRecursive = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(mapRecursive);
      }

      if (typeof obj === 'object' && obj !== '') {
        const newObj: any = {};

        for (const [key, value] of Object.entries(obj)) {
          if (keys.includes(key)) {
            if (value === 0 && !ignoreZero) {
              newObj[key] = '';
            } else if (value === '' || value === null) {
              newObj[key] = 0;
            } else {
              newObj[key] = value;
            }
          } else if (typeof value === 'object') {
            newObj[key] = mapRecursive(value);
          } else {
            newObj[key] = value;
          }
        }

        return newObj;
      }

      return obj;
    };

    return mapRecursive(data);
  }
  public isUnitConversionRequired(key: string): boolean {
    return UNIT_CONVERSION_REQUIRED_KEYS.includes(key);
  }

  public convertToMiles(value: number, preferredUnit: string): number {
    const miles = preferredUnit === 'Kilometers' ? value * 0.621371 : value;
    return +miles.toFixed(2);
  }

  public convertToKm(value: number, preferredUnit: string): number {
    const km = preferredUnit === 'Miles' ? value * 1.60934 : value;
    return +km.toFixed(2);
  }
  private buildGpioConfigListForPayload(config: any): any[] | undefined {
    if (!config) return undefined;

    // ✅ Support both nested (gpioTrigger.xxx) and flat (xxx) structures
    const gpioConfig = config.gpioTrigger || config;

    const trigger = (gpioConfig.gpIoSource ?? config.gpIoSource) as 'direct' | 'driverIDBased' | 'eventBased' | undefined;

    const enabledFlag = gpioConfig.gpIoTriggerEnabled ?? config.gpIoTriggerEnabled;

    // ⛔ Only require trigger – NOT enabled
    if (!trigger) {
      return undefined;
    }

    const gpio: any = {
      trigger,
      // 👇 This will be false when gpIoTriggerEnabled is false
      enabled: !!enabledFlag,
    };

    // Helper to get value from either nested or flat structure
    const getValue = (key: string) => gpioConfig[key] ?? config[key];

    // ✅ Pin defaults to 1 if not provided
    const deviceOutPinNumber =
      getValue('deviceOutPinNumber') ?? getValue('gpioDeviceOutPinNumber') ?? getValue('gpIoDeviceOutPinNumber') ?? 1;

    // ✅ Always set deviceOutPinNumber (will use default 1 if nothing provided)
    gpio.deviceOutPinNumber = deviceOutPinNumber;

    const setIfPresent = (key: string, value: any) => {
      if (value === null || value === undefined) return;
      if (typeof value === 'string' && value.trim() === '') return;
      gpio[key] = value;
    };

    // 🆕 Always set deviceOutPinNumber (will use default 1 if nothing provided)
    gpio.deviceOutPinNumber = deviceOutPinNumber;

    // direct / driverIDBased → maxSpeedThreshold
    if (trigger === 'direct' || trigger === 'driverIDBased') {
      setIfPresent('maxSpeedThreshold', getValue('maxSpeedThreshold'));
    }

    // eventBased → minSpeedThreshold, gpioPinDurationInSeconds, onEvent
    if (trigger === 'eventBased') {
      setIfPresent('minSpeedThreshold', getValue('minSpeedThreshold'));
      setIfPresent('gpioPinDurationInSeconds', getValue('gpioPinDurationInSeconds'));

      const events = getValue('dmsEventsForGpioTrigger');
      if (Array.isArray(events) && events.length > 0) {
        setIfPresent('onEvent', events);
      }
    }

    return [gpio];
  }
  /**
   * Transform gpioConfigList array from server into structure for form
   * Creates a NESTED gpioTrigger object (not flat keys)
   */
  private mapGpioConfigListToFlat(config: any): any {
    if (!config?.gpioConfigList || !Array.isArray(config.gpioConfigList) || config.gpioConfigList.length === 0) {
      // No GPIO config from server - return config as-is
      return config;
    }

    const gpio = config.gpioConfigList[0];

    const gpioTrigger: any = {
      gpIoTriggerEnabled: gpio.enabled ?? false,
      gpIoSource: gpio.trigger ?? null,
    };

    if (gpio.deviceOutPinNumber !== undefined) {
      gpioTrigger.deviceOutPinNumber = gpio.deviceOutPinNumber;
    }

    if (gpio.maxSpeedThreshold !== undefined) {
      gpioTrigger.maxSpeedThreshold = gpio.maxSpeedThreshold;
    }

    if (gpio.minSpeedThreshold !== undefined) {
      gpioTrigger.minSpeedThreshold = gpio.minSpeedThreshold;
    }

    if (gpio.onEvent !== undefined) {
      gpioTrigger.dmsEventsForGpioTrigger = gpio.onEvent;
    }

    if (gpio.gpioPinDurationInSeconds !== undefined) {
      gpioTrigger.gpioPinDurationInSeconds = gpio.gpioPinDurationInSeconds;
    }

    return {
      ...config,
      gpioTrigger,
    };
  }
}
